// AutocompleteDropdown — `/`-command suggestion list.
//
// Phase 3 Step 2c1 extraction. Pure consumer of `useDecisionEngine()`
// context for the highlight index + visibility flag; receives the already-
// filtered command list and the pick callback as props (the page owns the
// filtering memo because the same list is also consumed by the keyboard
// handler in `onInputKeyDown`).
//
// Renders nothing when the dropdown should be closed — keeps the parent
// JSX free of `showAutocomplete && ...` guards.
import React from 'react';

import { useDecisionEngine } from './state/DecisionEngineContext';

export const AutocompleteDropdown = ({ filteredCommands, onPick }) => {
  const { state, dispatch } = useDecisionEngine();
  const { showAutocomplete, autocompleteIdx } = state;
  if (!showAutocomplete || filteredCommands.length === 0) return null;

  return (
    <div
      className="absolute bottom-20 left-6 right-6 max-w-3xl mx-auto bg-white dark:bg-gray-900 border rounded-lg shadow-lg overflow-hidden"
      data-testid="command-autocomplete"
    >
      {filteredCommands.map((c, i) => (
        <button
          key={c.command}
          onClick={() => onPick(c)}
          onMouseEnter={() => dispatch({ autocompleteIdx: i })}
          className={`w-full text-left px-4 py-2 flex items-center gap-3 ${
            i === autocompleteIdx ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''
          }`}
          data-testid={`autocomplete-option-${c.command.slice(1)}`}
        >
          <code className="text-xs font-mono text-indigo-600 shrink-0">{c.command}</code>
          <span className="text-xs text-gray-500 truncate">{c.purpose}</span>
          {c.args && c.args !== 'none' && (
            <span className="text-xs text-gray-400 shrink-0">({c.args})</span>
          )}
        </button>
      ))}
      <div
        className="px-4 py-1.5 text-[10px] text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40"
        data-testid="command-autocomplete-hint"
        aria-hidden="true"
      >
        <kbd className="font-mono px-1 rounded bg-gray-100 dark:bg-gray-800">↑</kbd>{' '}
        <kbd className="font-mono px-1 rounded bg-gray-100 dark:bg-gray-800">↓</kbd>{' '}
        to navigate ·{' '}
        <kbd className="font-mono px-1 rounded bg-gray-100 dark:bg-gray-800">Tab</kbd>/
        <kbd className="font-mono px-1 rounded bg-gray-100 dark:bg-gray-800">Enter</kbd>{' '}
        to select
      </div>
    </div>
  );
};
